import axios from 'axios';

import { API_URL } from '../config';

const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    }
});

export interface ProjectEmployee {
  imageUrl: string;
  id: number;
  name: string;
  surname: string;
  email: string;
  pivot: {
    role: string | null;
    assigned_date: string;
    end_date: string | null;
  };
}

export interface Project {
  id: number;
  name: string;
  description: string | null;
  location: string;
  status: string;
  /** Planned start / end, YYYY-MM-DD; null when not set (older projects, mobile-created ones) */
  start_date: string | null;
  end_date: string | null;
  created_at: string;
  updated_at: string;
  current_employees: ProjectEmployee[];
  employees: ProjectEmployee[];
  plan_1: string | null;
  plan_2: string | null;
  plan_1_url: string | null;
  plan_2_url: string | null;
  files: ProjectFile[];
  /** Plan progress, list endpoint only. Sums can arrive as strings (MySQL SUM) or null (no tasks). */
  tasks_count?: number;
  tasks_total_percentage?: number | string | null;
  tasks_done_percentage?: number | string | null;
}

export type TaskStatus = 'ready' | 'in_progress' | 'done';
export const TASK_STATUSES: TaskStatus[] = ['ready', 'in_progress', 'done'];

/** One task/phase of a project's plan; all of a project's tasks together are worth at most 100%. */
export interface ProjectTask {
  id: number;
  project_id: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  percentage: number;
  position: number;
  created_at: string;
  updated_at: string;
}

export type ProjectTaskInput = Pick<ProjectTask, 'title' | 'description' | 'percentage' | 'status'>;

/** The board after a drag & drop: task ids per column, in column order. */
export type TaskColumns = Record<TaskStatus, number[]>;

/** What a client link may show besides name, location, status and dates (all off by default). */
export const SHARE_PERMISSIONS = [
  'show_documents',
  'show_plan',
  'show_photos_before',
  'show_photos_after',
  'show_team_days',
] as const;
export type SharePermission = (typeof SHARE_PERMISSIONS)[number];
export type ShareLanguage = 'de' | 'en' | 'sq';

/** A read-only client link to a project (web /p/<token>). */
export interface ProjectShareLink extends Record<SharePermission, boolean> {
  id: number;
  project_id: number;
  token: string;
  label: string | null;
  language: ShareLanguage;
  views_count: number;
  last_viewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ShareLinkInput = Partial<Pick<ProjectShareLink, 'label' | 'language' | SharePermission>>;

/** The address the client opens (this panel's /p/<token> page). */
export const shareLinkUrl = (token: string) => `${window.location.origin}/p/${token}`;

export interface ProjectFile {
  id: number;
  project_id: number;
  file_name: string;
  file_path: string;
  file_url: string;
  created_at: string;
  updated_at: string;
}

export interface Attendance {
  id: number;
  employee_id: number;
  project_id: number;
  check_in: string;
  check_out: string | null;
  duration_seconds: number | null;
  employee: {
    id: number;
    name: string;
    surname: string;
  };
}

export interface ProjectAttendance {
  data: Attendance[];
  total_duration_seconds: number;
}

export type PhotoType = 'before' | 'after';

/** One feed post with photos, as returned by GET /admin/projects/{id}/feed-images. */
export interface ProjectPhotoPost {
  id: number;
  user_id: number;
  type: PhotoType;
  description?: string | null;
  materials_used?: string | null;
  created_at: string;
  user: {
    id: number;
    name: string;
    employee?: {
      id: number;
      name: string;
      surname: string;
      imageUrl?: string;
      thumbnailUrl?: string;
    } | null;
  } | null;
  images: {
    id: number;
    image_path: string;
    imageUrl: string;
    /** 800px copy; the full image for old uploads */
    thumbnailUrl: string;
  }[];
}

export interface PhotoFilters {
  user_id?: number;
  type?: PhotoType;
}

class ProjectService {
  private baseUrl = `${API_URL}/admin/projects`;

  async getProjects(): Promise<Project[]> {
    const response = await api.get(this.baseUrl);
    return response.data.data;
  }

  async getProject(id: number): Promise<Project> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async createProject(projectData: Partial<Project>): Promise<Project> {
    const response = await api.post(this.baseUrl, projectData);
    return response.data.data;
  }

  async updateProject(id: number, projectData: Partial<Project>): Promise<Project> {
    const response = await api.put(`${this.baseUrl}/${id}`, projectData);
    return response.data.data;
  }

  async deleteProject(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  async assignEmployee(projectId: number, employeeId: number, role?: string): Promise<Project> {
    const response = await api.post(`${this.baseUrl}/${projectId}/assign-employee`, {
      employee_id: employeeId,
      role
    });
    return response.data.data;
  }

  async removeEmployee(projectId: number, employeeId: number): Promise<Project> {
    const response = await api.post(`${this.baseUrl}/${projectId}/remove-employee`, {
      employee_id: employeeId
    });
    return response.data.data;
  }

  async storeFirstPlan(projectId: number, file: File): Promise<Project> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${this.baseUrl}/${projectId}/store-first-plan`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async storeSecondPlan(projectId: number, file: File): Promise<Project> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${this.baseUrl}/${projectId}/store-second-plan`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async storeFile(projectId: number, file: File, file_name: string): Promise<{ status: string; message: string; data: ProjectFile }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_name', file_name);
    const response = await api.post(`${this.baseUrl}/${projectId}/store-file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteFile(fileId: number): Promise<{ status: string; message: string }> {
    const response = await api.delete(`${this.baseUrl}/files/${fileId}`);
    return response.data;
  }

  async getAttendanceForProject(projectId: number): Promise<ProjectAttendance> {
    const response = await api.get(`${this.baseUrl}/${projectId}/attendance`);
    return response.data;
  }

  async getMonthlyAssignments(projectId: number | string, month: number, year: number): Promise<any[]> {
    const response = await api.get(`${this.baseUrl}/${projectId}/monthly-assignments`, {
      params: { month, year }
    });
    return response.data.data;
  }

  // ---------- Plan (tasks board) ----------

  async getTasks(projectId: number): Promise<ProjectTask[]> {
    const response = await api.get(`${this.baseUrl}/${projectId}/tasks`);
    return response.data.data;
  }

  async createTask(projectId: number, task: ProjectTaskInput): Promise<ProjectTask> {
    const response = await api.post(`${this.baseUrl}/${projectId}/tasks`, task);
    return response.data.data;
  }

  async updateTask(taskId: number, task: Partial<ProjectTaskInput>): Promise<ProjectTask> {
    const response = await api.put(`${API_URL}/admin/project-tasks/${taskId}`, task);
    return response.data.data;
  }

  async deleteTask(taskId: number): Promise<void> {
    await api.delete(`${API_URL}/admin/project-tasks/${taskId}`);
  }

  /** Saves every column's order (and so each task's status); returns the saved board. */
  async reorderTasks(projectId: number, columns: TaskColumns): Promise<ProjectTask[]> {
    const response = await api.put(`${this.baseUrl}/${projectId}/tasks/reorder`, { columns });
    return response.data.data;
  }

  // ---------- Client links ----------

  async getShareLinks(projectId: number): Promise<ProjectShareLink[]> {
    const response = await api.get(`${this.baseUrl}/${projectId}/share-links`);
    return response.data.data;
  }

  async createShareLink(projectId: number, link: ShareLinkInput): Promise<ProjectShareLink> {
    const response = await api.post(`${this.baseUrl}/${projectId}/share-links`, link);
    return response.data.data;
  }

  async updateShareLink(linkId: number, link: ShareLinkInput): Promise<ProjectShareLink> {
    const response = await api.put(`${API_URL}/admin/project-share-links/${linkId}`, link);
    return response.data.data;
  }

  /** Revokes the link: the client's URL stops working. */
  async deleteShareLink(linkId: number): Promise<void> {
    await api.delete(`${API_URL}/admin/project-share-links/${linkId}`);
  }

  /** Every post with photos on the project, newest first (unfiltered — the drawer filters). */
  async getFeedImages(projectId: number): Promise<ProjectPhotoPost[]> {
    const response = await api.get(`${this.baseUrl}/${projectId}/feed-images`);
    return response.data.data;
  }

  /** The photos matching the filters as one ZIP. On an error the server's JSON `message` is thrown. */
  async downloadFeedImagesZip(projectId: number, filters: PhotoFilters): Promise<Blob> {
    try {
      const response = await api.get(`${this.baseUrl}/${projectId}/feed-images/zip`, {
        params: filters,
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      // With responseType 'blob' the error body is a Blob too
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text());
          if (parsed?.message) throw new Error(parsed.message);
        } catch (parseError) {
          if (parseError instanceof Error && !(parseError instanceof SyntaxError)) throw parseError;
        }
      }
      throw error;
    }
  }
}

export const projectService = new ProjectService();